import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, type Season } from '../../services/api';
import { toast } from '../shared/Toast';
import '../shared/ConfirmDialog';

@customElement('season-manager')
export class SeasonManager extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state()
  private seasons: Season[] = [];

  @state()
  private loading = true;

  @state()
  private showForm = false;

  @state()
  private editingSeason: Season | null = null;

  @state()
  private formName = '';

  @state()
  private formYear = new Date().getFullYear();

  @state()
  private formStartDate = '';

  @state()
  private formEndDate = '';

  @state()
  private saving = false;

  @state()
  private searchQuery = '';

  // Delete confirmation
  @state()
  private deleteConfirmOpen = false;

  @state()
  private deletingSeason: Season | null = null;

  @state()
  private deleting = false;

  // Form validation
  @state()
  private formTouched = false;

  connectedCallback() {
    super.connectedCallback();
    this.loadSeasons();
  }

  private async loadSeasons() {
    this.loading = true;
    try {
      this.seasons = (await api.getSeasons()) || [];
      // Sort by year descending
      this.seasons.sort((a, b) => b.year - a.year);
    } catch (error) {
      console.error('Failed to load seasons:', error);
      toast.error('Failed to load seasons');
    } finally {
      this.loading = false;
    }
  }

  private get filteredSeasons(): Season[] {
    if (!this.searchQuery) return this.seasons;
    const query = this.searchQuery.toLowerCase();
    return this.seasons.filter(
      s => s.name.toLowerCase().includes(query) ||
           s.year.toString().includes(query)
    );
  }

  private get formNameError(): string | null {
    if (!this.formTouched) return null;
    if (!this.formName.trim()) return 'Name is required';
    return null;
  }

  private get formDateError(): string | null {
    if (!this.formStartDate || !this.formEndDate) return null;
    if (new Date(this.formEndDate) < new Date(this.formStartDate)) {
      return 'End date must be after start date';
    }
    return null;
  }

  private openForm(season?: Season) {
    this.editingSeason = season || null;
    this.formName = season?.name || '';
    this.formYear = season?.year || new Date().getFullYear();
    this.formStartDate = season?.start_date?.slice(0, 10) || '';
    this.formEndDate = season?.end_date?.slice(0, 10) || '';
    this.showForm = true;
    this.formTouched = false;
  }

  private closeForm() {
    this.showForm = false;
    this.editingSeason = null;
    this.formName = '';
    this.formYear = new Date().getFullYear();
    this.formStartDate = '';
    this.formEndDate = '';
    this.formTouched = false;
  }

  private handleStartDateChange(value: string) {
    this.formStartDate = value;
    // Auto-fill year from start date
    if (value) {
      this.formYear = new Date(value).getFullYear();
    }
  }

  private async saveSeason() {
    this.formTouched = true;
    if (this.formNameError || this.formDateError) return;

    this.saving = true;
    try {
      const data = {
        name: this.formName.trim(),
        year: this.formYear,
        start_date: this.formStartDate || undefined,
        end_date: this.formEndDate || undefined,
      };

      if (this.editingSeason) {
        await api.updateSeason(this.editingSeason.id, data);
        toast.success(`Season "${this.formName}" updated`);
      } else {
        await api.createSeason(data);
        toast.success(`Season "${this.formName}" created`);
      }
      await this.loadSeasons();
      this.closeForm();
      this.dispatchEvent(new CustomEvent('seasons-changed', { bubbles: true, composed: true }));
    } catch (error) {
      console.error('Failed to save season:', error);
      toast.error('Failed to save season');
    } finally {
      this.saving = false;
    }
  }

  private confirmDelete(season: Season) {
    this.deletingSeason = season;
    this.deleteConfirmOpen = true;
  }

  private cancelDelete() {
    this.deleteConfirmOpen = false;
    this.deletingSeason = null;
  }

  private async executeDelete() {
    if (!this.deletingSeason) return;

    this.deleting = true;
    try {
      await api.deleteSeason(this.deletingSeason.id);
      toast.success(`Season "${this.deletingSeason.name}" deleted`);
      await this.loadSeasons();
      this.cancelDelete();
      this.dispatchEvent(new CustomEvent('seasons-changed', { bubbles: true, composed: true }));
    } catch (error) {
      console.error('Failed to delete season:', error);
      toast.error('Failed to delete season');
    } finally {
      this.deleting = false;
    }
  }

  private async setAsDefault(season: Season) {
    if (season.is_default) return;
    try {
      await api.setDefaultSeason(season.id);
      toast.success(`"${season.name}" is now the default season`);
      await this.loadSeasons();
      this.dispatchEvent(new CustomEvent('seasons-changed', { bubbles: true, composed: true }));
    } catch (error) {
      console.error('Failed to set default season:', error);
      toast.error('Failed to set default season');
    }
  }

  render() {
    return html`
      <div class="space-y-4">
        <div class="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          <h3 class="text-lg font-semibold">Seasons (${this.seasons.length})</h3>
          <div class="flex gap-2">
            <input
              type="text"
              class="input input-bordered input-sm flex-1 min-w-0 sm:w-48"
              placeholder="Search seasons..."
              .value=${this.searchQuery}
              @input=${(e: Event) => this.searchQuery = (e.target as HTMLInputElement).value}
            />
            <button class="btn btn-primary btn-sm whitespace-nowrap" @click=${() => this.openForm()}>
              Add Season
            </button>
          </div>
        </div>

        <!-- Loading skeleton -->
        ${this.loading ? html`
          <div class="space-y-2">
            ${[1, 2, 3].map(() => html`
              <div class="skeleton h-12 w-full"></div>
            `)}
          </div>
        ` : html`
          <!-- Form -->
          ${this.showForm ? this.renderForm() : nothing}

          <!-- Seasons list -->
          ${this.renderSeasonsList()}

          <!-- Empty state -->
          ${this.filteredSeasons.length === 0 ? html`
            <p class="text-center text-base-content/70 py-4">
              ${this.searchQuery ? 'No seasons match your search.' : 'No seasons yet. Create one to organize games.'}
            </p>
          ` : nothing}
        `}

        <!-- Delete confirmation dialog -->
        <confirm-dialog
          .open=${this.deleteConfirmOpen}
          title="Delete Season"
          .message=${`Delete "${this.deletingSeason?.name}"? Games in this season will lose their season association.`}
          confirmText="Delete"
          .destructive=${true}
          .loading=${this.deleting}
          @confirm=${this.executeDelete}
          @cancel=${this.cancelDelete}
        ></confirm-dialog>
      </div>
    `;
  }

  private renderForm() {
    return html`
      <div class="card bg-base-200">
        <div class="card-body">
          <h4 class="card-title">${this.editingSeason ? 'Edit Season' : 'New Season'}</h4>
          <div class="grid gap-4 md:grid-cols-2">
            <div class="form-control">
              <label class="label"><span class="label-text">Name *</span></label>
              <input
                type="text"
                class="input input-bordered ${this.formNameError ? 'input-error' : ''}"
                placeholder="e.g., Spring 2024"
                .value=${this.formName}
                @input=${(e: Event) => {
                  this.formName = (e.target as HTMLInputElement).value;
                  this.formTouched = true;
                }}
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === 'Enter') this.saveSeason();
                  if (e.key === 'Escape') this.closeForm();
                }}
              />
              ${this.formNameError ? html`
                <label class="label">
                  <span class="label-text-alt text-error">${this.formNameError}</span>
                </label>
              ` : nothing}
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Year *</span></label>
              <input
                type="number"
                class="input input-bordered"
                min="2020"
                max="2030"
                .value=${this.formYear.toString()}
                @input=${(e: Event) => this.formYear = parseInt((e.target as HTMLInputElement).value, 10)}
              />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Start Date</span></label>
              <input
                type="date"
                class="input input-bordered ${this.formDateError ? 'input-error' : ''}"
                .value=${this.formStartDate}
                @input=${(e: Event) => this.handleStartDateChange((e.target as HTMLInputElement).value)}
              />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">End Date</span></label>
              <input
                type="date"
                class="input input-bordered ${this.formDateError ? 'input-error' : ''}"
                .value=${this.formEndDate}
                .min=${this.formStartDate}
                @input=${(e: Event) => this.formEndDate = (e.target as HTMLInputElement).value}
              />
              ${this.formDateError ? html`
                <label class="label">
                  <span class="label-text-alt text-error">${this.formDateError}</span>
                </label>
              ` : nothing}
            </div>
          </div>
          <div class="card-actions justify-end mt-4">
            <button class="btn btn-ghost" @click=${this.closeForm}>Cancel</button>
            <button
              class="btn btn-primary"
              @click=${this.saveSeason}
              ?disabled=${this.saving}
            >
              ${this.saving ? html`<span class="loading loading-spinner loading-sm"></span>` : 'Save'}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderSeasonsList() {
    if (this.filteredSeasons.length === 0) return nothing;

    return html`
      <div class="overflow-x-auto">
        <table class="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Year</th>
              <th>Dates</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            ${this.filteredSeasons.map((season) => html`
              <tr class="hover">
                <td class="font-semibold">
                  <div class="flex items-center gap-2">
                    <span>${season.name}</span>
                    ${season.is_default ? html`
                      <span class="badge badge-primary badge-sm" title="Default season" aria-label="Default season">
                        <span aria-hidden="true">&#9733;</span>
                        <span class="ml-1">Default</span>
                      </span>
                    ` : nothing}
                  </div>
                </td>
                <td>${season.year}</td>
                <td class="text-base-content/70">
                  ${season.start_date && season.end_date
                    ? `${new Date(season.start_date).toLocaleDateString()} - ${new Date(season.end_date).toLocaleDateString()}`
                    : '-'}
                </td>
                <td class="text-right">
                  <button
                    class="btn btn-ghost btn-xs"
                    @click=${() => this.setAsDefault(season)}
                    ?disabled=${season.is_default}
                    title=${season.is_default ? 'Already the default season' : 'Mark this season as the default'}
                  >Set as default</button>
                  <button class="btn btn-ghost btn-xs" @click=${() => this.openForm(season)}>Edit</button>
                  <button class="btn btn-ghost btn-xs text-error" @click=${() => this.confirmDelete(season)}>Delete</button>
                </td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }
}
